import request from "@/providers/requests";
import ENDPOINTS from "@/providers/end-points";
import Storage from "@/services/storage";
import { SessionInterface } from "@/interface/user.interface";
import { SessionModel } from "@/modules/user";
import { StorageConstants } from "@/enums/storage.enum";

const UserStore = {
  namespaced: true,
  state: () => ({
    session: null,
    message: null,
    error: null,
    success: false,
    isLoggedIn: false,
    isAuthorized: false,
  }),
  getters: {
    isLoggedIn: (state: any): boolean => state.isLoggedIn,
    session: (state: any): any => state.session,
    error: (state: any) => state.error,
    success: (state: any) => state.success,
    message: (state: any) => state.message,
    isAuthorized: (state: any) => state.isAuthorized,
  },
  mutations: {
    setUserLoggedIn(state: any, isLoggedIn: Boolean) {
      state.isLoggedIn = isLoggedIn;
      state.isAuthorized = isLoggedIn;
    },

    setUserData(state: any, session: any) {
      state.session = session;
    },

    setError(state: any, error: []) {
      state.error = error;
    },

    setSuccess(state: any, success: boolean = false, message: string = "") {
      state.success = success;
      state.message = message;
    },

    SET_IS_USER_AUTHORIZED(state: any, payload) {
      state.isAuthorized = payload;
    },

    SET_IS_USER_MESSAGE(state: any, payload) {
      state.success = payload.success;
      state.message = payload.message;
    },
  },
  actions: {
    /**
     * Handle SSO each time user back to site
     * @param store
     * @return {void}
     */
    async singleSignOn({ commit }: { commit: any }) {
      const user = Storage.get(StorageConstants.SESSION);
      if (!!user) {
        await commit("setUserData", user);
        await commit("setUserLoggedIn", true);
      }
    },

    /**
     * Handle login request
     * @param store
     * @param user
     * @return {<Promise>}
     */
    async doLogin(store: any, user: any) {
      await store.commit("setUserData", null);
      await store.commit("setUserLoggedIn", false);

      return await request
        .post(ENDPOINTS.LOGIN, user)
        .then((response) => response.data)
        .then(async ({ data }: { data: SessionInterface }) => {
          const user = new SessionModel(data);

          // save user data
          await Storage.set(StorageConstants.TOKEN, user.getToken());
          await Storage.set(StorageConstants.SESSION, user.getUser());
          await Storage.set(StorageConstants.PERMISSION, user.getPermissions());

          // prepare user business data
          await store.dispatch("Business/getBusiness", null, { root: true });
          await store.dispatch("Business/getLocations", null, { root: true });

          // trigger store user is logged in
          await store.commit("setUserData", user.getUser());
          await store.commit("setUserLoggedIn", true);
        })
        .catch(async ({ error }: { error: any }) => {
          await store.commit("setSuccess", false, error?.message);
          await store.commit("setError", error);
        });
    },

    /**
     * Handle reset user password
     * @param Store
     * @param user
     * @return {<Promise>}
     */
    async doResetPassword({ commit }: { commit: any }, user: any) {
      return await request
        .post(ENDPOINTS.RESET_PASSWORD, user)
        .then((response) => response.data)
        .then(async (data) => {
          // save user information on app storage
          await commit("setSuccess", true);
        })
        .catch(async (error: any) => {
          await commit("setSuccess", false);
          await commit("setError", error);
        });
    },

    /**
     * Handle user logout
     * @param Store
     * @return {void}
     */
    async doLogOut({ commit }: { commit: any }) {
      Storage.remove(StorageConstants.SESSION);
      Storage.remove(StorageConstants.TOKEN);
      Storage.remove(StorageConstants.BUSINESS);
      Storage.remove(StorageConstants.LOCATIONS);
      Storage.remove(StorageConstants.CITIES);
      await commit("setUserData", null);
      await commit("setUserLoggedIn", false);
    },

    /**
     * Handle reset user data
     * @param Store
     * @return {void}
     */
    async doResetUserData({ commit }: { commit: any }) {
      Storage.remove(StorageConstants.SESSION);
      Storage.remove(StorageConstants.TOKEN);
      Storage.remove(StorageConstants.BUSINESS);
      Storage.remove(StorageConstants.LOCATIONS);
      await commit("setUserData", null);
      await commit("setUserLoggedIn", false);
    },
  },
};

export default UserStore;
